(() => {
  const loader = document.getElementById('DelayedAppsLoader');
  if (!loader) return;
  const isProductPage = loader.dataset.productPage === 'true';

  const isChattyFont = (node) =>
    node?.tagName === 'LINK' &&
    /fonts\.bunny\.net/i.test(node.href || '') &&
    /family=Inter/i.test(node.href || '');

  if (isProductPage) {
    document.querySelectorAll('link[href*="fonts.bunny.net"]').forEach((link) => {
      if (isChattyFont(link)) link.remove();
    });

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (isChattyFont(node)) node.remove();
        });
      });
    }).observe(document.head, { childList: true });
  }

  const readDelay = (value, fallback) => {
    const delay = Number.parseInt(value || String(fallback), 10);
    return Number.isFinite(delay) ? delay : fallback;
  };

  const loadScript = (src, force = false) =>
    new Promise((resolve) => {
      const absoluteSource = new URL(src, document.baseURI).href;
      if (!force && Array.from(document.scripts).some((script) => script.src === absoluteSource)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });

  const syncCartDrawerState = () => {
    document.documentElement.classList.toggle(
      'll-cart-drawer-open',
      Boolean(document.querySelector('cart-drawer.active')),
    );
  };

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    syncCartDrawerState();
    new MutationObserver(syncCartDrawerState).observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  let klaviyoObserver;
  let klaviyoFrame = 0;
  const klaviyoScopes = new Set();
  const klaviyoSelector = '[class*="klaviyo" i], [id*="klaviyo" i]';

  const markKlaviyoWidgets = (scopes) => {
    const candidates = new Set();
    const roots = new Set();
    const teasers = new Map();
    const measurements = new WeakMap();

    scopes.forEach((scope) => {
      if (!scope.isConnected) return;
      if (scope.matches?.(klaviyoSelector)) candidates.add(scope);
      scope.querySelectorAll(klaviyoSelector).forEach((element) => candidates.add(element));
    });

    // Read each shared ancestor once per batch; write markers after all reads.
    candidates.forEach((element) => {
      if (element === document.body || element === document.documentElement) return;

      let root = element;
      while (root.parentElement && root.parentElement !== document.body) {
        root = root.parentElement;
      }
      if (root.parentElement === document.body) roots.add(root);

      let positioned = element;
      while (positioned && positioned !== document.body) {
        if (!measurements.has(positioned)) {
          const fixed = getComputedStyle(positioned).position === 'fixed';
          measurements.set(positioned, fixed ? positioned.getBoundingClientRect() : null);
        }
        const rect = measurements.get(positioned);
        if (
          rect &&
          rect.width > 0 &&
          rect.height > 0 &&
          rect.width <= 360 &&
          rect.height <= 360
        ) {
          teasers.set(positioned, rect.left <= window.innerWidth - rect.right ? 'left' : 'right');
          break;
        }
        positioned = positioned.parentElement;
      }
    });

    teasers.forEach((side, teaser) => {
      if (teaser.dataset.llKlaviyoTeaser !== 'true') teaser.dataset.llKlaviyoTeaser = 'true';
      if (teaser.dataset.llKlaviyoSide !== side) teaser.dataset.llKlaviyoSide = side;
    });
    roots.forEach((root) => {
      if (root.dataset.llKlaviyoRoot !== 'true') root.dataset.llKlaviyoRoot = 'true';
    });
  };

  const flushKlaviyoWidgets = () => {
    klaviyoFrame = 0;
    const scopes = Array.from(klaviyoScopes);
    klaviyoScopes.clear();
    markKlaviyoWidgets(scopes);
  };

  const watchKlaviyoWidgets = () => {
    if (klaviyoObserver) return;
    markKlaviyoWidgets([document]);

    klaviyoObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Recheck a known widget when its own content changes, not on every
        // unrelated review/cart mutation elsewhere in the document.
        const widget = mutation.target.closest?.(klaviyoSelector) ||
          mutation.target.closest?.('[data-ll-klaviyo-root]');
        if (widget && widget !== document.body) klaviyoScopes.add(widget);
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) klaviyoScopes.add(node);
        });
      });
      if (klaviyoScopes.size && !klaviyoFrame) {
        klaviyoFrame = window.requestAnimationFrame(flushKlaviyoWidgets);
      }
    });
    klaviyoObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      klaviyoObserver.disconnect();
      if (klaviyoFrame) window.cancelAnimationFrame(klaviyoFrame);
      flushKlaviyoWidgets();
    }, 60000);
  };

  const runWhenIdle = (task) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(task, { timeout: 1500 });
    } else {
      window.setTimeout(task, 0);
    }
  };

  const schedule = (delay, task) => {
    if (window.Shopify?.designMode) {
      runWhenIdle(task);
      return;
    }

    window.setTimeout(() => runWhenIdle(task), delay);
  };

  let chattyReleaseAttempts = 0;
  const loadChatty = () => {
    const currentChattySource = Array.from(document.scripts)
      .map((script) => script.src)
      .find((source) => /\/avada-faqs-\d+\/assets\/chatty\.js(?:\?|$)/i.test(source));

    if (currentChattySource) {
      const released = window.__releaseChattyByTheme?.();
      if (released) {
        loadScript(currentChattySource, true);
        return;
      }
    }

    if (chattyReleaseAttempts >= 10) return;

    chattyReleaseAttempts += 1;
    window.setTimeout(loadChatty, 500);
  };

  const loadGoogle = () => {
    if (!loader.dataset.googleSrc || !loader.dataset.googleMerchantId) return;

    loadScript(loader.dataset.googleSrc).then(() => {
      if (!window.merchantwidget?.start) return;

      window.merchantwidget.start({
        position: loader.dataset.googlePosition || 'RIGHT_BOTTOM',
        sideMargin: loader.dataset.googleSideMargin || '36',
        bottomMargin: loader.dataset.googleBottomMargin || '4',
        mobileSideMargin: loader.dataset.googleMobileSideMargin || '16',
        mobileBottomMargin: loader.dataset.googleMobileBottomMargin || '46',
        merchant_id: loader.dataset.googleMerchantId,
      });
    });
  };

  const loadKlaviyo = () => {
    if (loader.dataset.klaviyoSrc) {
      loadScript(loader.dataset.klaviyoSrc).then(watchKlaviyoWidgets);
    }
  };

  const scheduleApps = () => {
    schedule(readDelay(loader.dataset.chattyDelay, 3000), loadChatty);
    schedule(readDelay(loader.dataset.googleDelay, 6000), loadGoogle);
    schedule(readDelay(loader.dataset.klaviyoDelay, 10000), loadKlaviyo);
  };

  if (window.Shopify?.designMode) {
    scheduleApps();
    return;
  }

  let heroPaintConfirmed = false;
  const confirmHeroPaint = () => {
    if (heroPaintConfirmed) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (heroPaintConfirmed) return;
        heroPaintConfirmed = true;
        scheduleApps();
      });
    });
  };
  const heroImage = document.querySelector('.product__media img[fetchpriority="high"]');
  const confirmDecodedHeroPaint = () => {
    if (typeof heroImage?.decode !== 'function') {
      confirmHeroPaint();
      return;
    }

    heroImage.decode().then(confirmHeroPaint).catch(confirmHeroPaint);
  };

  if (heroImage?.complete && heroImage.naturalWidth > 0) {
    confirmDecodedHeroPaint();
  } else if (heroImage) {
    heroImage.addEventListener('load', confirmDecodedHeroPaint, { once: true });
    heroImage.addEventListener('error', confirmHeroPaint, { once: true });
  } else if (document.readyState === 'complete') {
    confirmHeroPaint();
  } else {
    window.addEventListener('load', confirmHeroPaint, { once: true });
  }

  // Broken extensions or unusual media must not suppress essential storefront
  // integrations forever. This fallback is deliberately later than the
  // slowest measured product LCP.
  window.setTimeout(confirmHeroPaint, 15000);
})();
